'use client'

import * as React from 'react'
import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'

import cn from '@config/tailwind/cn'

function Avatar({
  className,
  size = 'default',
  profile = 'user',
  ...props
}: AvatarPrimitive.Root.Props & {
  size?: 'default' | 'sm' | 'lg'
  profile?: 'user' | 'organization'
}) {
  return (
    <AvatarPrimitive.Root
      data-slot='avatar'
      data-size={size}
      data-pibo-avatar={profile}
      className={cn(
        'ui:border-outline-variant/50 ui:border ui:group/avatar ui:relative ui:flex ui:size-8 ui:shrink-0 ui:select-none ui:after:absolute ui:after:mix-blend-darken ui:after:data-[pibo-avatar=organization]:rounded-lg ui:after:data-[pibo-avatar=organization]:data-[size=sm]:rounded-sm ui:after:data-[pibo-avatar=user]:rounded-full ui:data-[size=lg]:size-10 ui:data-[size=sm]:size-6 ui:data-[pibo-avatar=organization]:rounded-lg ui:data-[pibo-avatar=organization]:data-[size=sm]:rounded-sm ui:data-[pibo-avatar=user]:rounded-full',
        className,
      )}
      {...props}
    />
  )
}

function AvatarImage({ className, ...props }: AvatarPrimitive.Image.Props) {
  return (
    <AvatarPrimitive.Image
      data-slot='avatar-image'
      className={cn(
        'ui:aspect-square ui:size-full ui:object-cover ui:group-data-[pibo-avatar=organization]/avatar:rounded-lg ui:group-data-[pibo-avatar=organization]/avatar:group-data-[size=sm]/avatar:rounded-sm ui:group-data-[pibo-avatar=user]/avatar:rounded-full',
        className,
      )}
      {...props}
    />
  )
}

function AvatarFallback({ className, ...props }: AvatarPrimitive.Fallback.Props) {
  return (
    <AvatarPrimitive.Fallback
      data-slot='avatar-fallback'
      className={cn(
        'ui:bg-linear-to-b ui:from-blue-100 ui:to-blue-70 ui:text-neutral-white ui:flex ui:size-full ui:items-center ui:justify-center ui:text-sm ui:group-data-[size=sm]/avatar:text-xs ui:group-data-[pibo-avatar=organization]/avatar:rounded-lg ui:group-data-[pibo-avatar=organization]/avatar:group-data-[size=sm]/avatar:rounded-sm ui:group-data-[pibo-avatar=user]/avatar:rounded-full',
        className,
      )}
      {...props}
    />
  )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='avatar-badge'
      className={cn(
        'ui:bg-blue-100 ui:text-neutral-white ui:ring-grey-20 ui:absolute ui:right-0 ui:bottom-0 ui:z-10 ui:inline-flex ui:items-center ui:justify-center ui:rounded-full ui:bg-blend-color ui:ring-2 ui:select-none',
        'ui:group-data-[size=sm]/avatar:size-2 ui:group-data-[size=sm]/avatar:[&>svg]:hidden',
        'ui:group-data-[size=default]/avatar:size-2.5 ui:group-data-[size=default]/avatar:[&>svg]:size-2',
        'ui:group-data-[size=lg]/avatar:size-3 ui:group-data-[size=lg]/avatar:[&>svg]:size-2',
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='avatar-group'
      className={cn(
        'ui:*:data-[slot=avatar]:ring-grey-20 ui:group/avatar-group ui:flex ui:-space-x-2 ui:*:data-[slot=avatar]:ring-2',
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='avatar-group-count'
      className={cn(
        'ui:bg-grey-20 ui:text-grey-100 ui:ring-grey-20 ui:relative ui:flex ui:size-8 ui:shrink-0 ui:items-center ui:justify-center ui:rounded-full ui:text-sm ui:ring-2 ui:group-has-data-[size=lg]/avatar-group:size-10 ui:group-has-data-[size=sm]/avatar-group:size-6 ui:[&>svg]:size-4 ui:group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 ui:group-has-data-[size=sm]/avatar-group:[&>svg]:size-3',
        className,
      )}
      {...props}
    />
  )
}

export { Avatar, AvatarImage, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarBadge }
